package dev.kotlinfest._2026.components.layouts

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.OverflowWrap
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.data.getValue
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobwebx.markdown.markdown
import dev.kotlinfest._2026.ContentContainerStyle
import dev.kotlinfest._2026.JapaneseFontFamily
import dev.kotlinfest._2026.LatinFontFamily
import dev.kotlinfest._2026.SiteLayout
import dev.kotlinfest._2026.SitePalette
import dev.kotlinfest._2026.components.widgets.SiteButtonArrowPath
import dev.kotlinfest._2026.components.widgets.SiteButtonIconSizePx
import dev.kotlinfest._2026.components.widgets.SiteButtonLanguage
import dev.kotlinfest._2026.components.widgets.SiteButtonSize
import dev.kotlinfest._2026.components.widgets.siteButtonHover
import dev.kotlinfest._2026.components.widgets.siteButtonSize
import dev.kotlinfest._2026.components.widgets.siteButtonText
import dev.kotlinfest._2026.components.widgets.sitePrimaryButtonActive
import dev.kotlinfest._2026.components.widgets.sitePrimaryButtonAppearance
import dev.kotlinfest._2026.desktopPageHorizontalPadding
import dev.kotlinfest._2026.sitePageHero
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Text

private class MarkdownPageData(
    val title: String,
    val subtitle: String?,
)

val MarkdownPageHeroStyle = CssStyle {
    base {
        Modifier.sitePageHero(240)
    }
    Breakpoint.MD {
        Modifier.sitePageHero(380)
    }
}

val MarkdownPageHeroContentStyle = CssStyle {
    base {
        Modifier
            .fillMaxSize()
            .padding(top = 40.px, leftRight = SiteLayout.MobileMarginPx.px)
            .styleModifier { property("box-sizing", "border-box") }
    }
    Breakpoint.MD {
        Modifier.padding(top = 40.px).desktopPageHorizontalPadding()
    }
}

val MarkdownEnglishHeroTitleStyle = CssStyle {
    base {
        Modifier
            .margin(0.px)
            .fontFamily(LatinFontFamily, "sans-serif")
            .fontSize(48.px)
            .fontWeight(600)
            .lineHeight(1.2)
            .letterSpacing(0.08.em)
            .textAlign(TextAlign.Center)
    }
}

val MarkdownJapaneseHeroTitleStyle = CssStyle {
    base {
        Modifier
            .margin(0.px)
            .fontFamily(JapaneseFontFamily, "sans-serif")
            .fontSize(26.px)
            .fontWeight(700)
            .lineHeight(1.6)
            .letterSpacing(0.08.em)
            .textAlign(TextAlign.Center)
    }
    Breakpoint.MD {
        Modifier.fontSize(40.px)
    }
}

val MarkdownHeroSubtitleStyle = CssStyle {
    base {
        Modifier
            .fontFamily(JapaneseFontFamily, "sans-serif")
            .fontSize(14.px)
            .fontWeight(700)
            .lineHeight(1.6)
            .letterSpacing(0.1.em)
            .textAlign(TextAlign.Center)
    }
    Breakpoint.MD {
        Modifier.fontSize(16.px)
    }
}

val MarkdownContentSectionStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(
                top = 64.px,
                bottom = 64.px,
                leftRight = SiteLayout.MobileMarginPx.px,
            )
    }
    Breakpoint.MD {
        Modifier
            .padding(top = 120.px, bottom = 120.px)
            .desktopPageHorizontalPadding()
    }
}

private fun Modifier.markdownList(): Modifier = fillMaxWidth()
    .margin(top = 0.px, bottom = 24.px)
    .padding(left = 28.px)
    .styleModifier { property("box-sizing", "border-box") }

val MarkdownStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .fontFamily(JapaneseFontFamily, "sans-serif")
            .fontSize(16.px)
            .fontWeight(500)
            .lineHeight(1.6)
            .letterSpacing(0.1.em)
            .color(SitePalette.brand.ink)
            .overflowWrap(OverflowWrap.BreakWord)
    }

    cssRule("h2") {
        Modifier
            .fontSize(26.px)
            .fontWeight(700)
            .lineHeight(2.0)
            .letterSpacing(0.08.em)
            .margin(top = 32.px, bottom = 16.px)
    }

    cssRule("h3") {
        Modifier
            .fontSize(20.px)
            .fontWeight(700)
            .lineHeight(1.6)
            .letterSpacing(0.08.em)
            .margin(top = 24.px, bottom = 16.px)
    }

    cssRule("h4") {
        Modifier
            .fontSize(18.px)
            .fontWeight(700)
            .lineHeight(1.6)
            .margin(top = 16.px, bottom = 12.px)
    }

    cssRule("p") {
        Modifier.margin(top = 0.px, bottom = 24.px)
    }

    cssRule(" .silk-callout .callout-body > p:first-child") {
        Modifier.margin(top = 0.px)
    }

    cssRule(" .silk-callout .callout-body > p:last-child") {
        Modifier.margin(bottom = 0.px)
    }

    cssRule("ol") {
        Modifier.markdownList()
    }

    cssRule("ul") {
        Modifier.markdownList()
    }

    cssRule("li > ol") {
        Modifier.margin(top = 8.px, bottom = 0.px)
    }

    cssRule("li > ul") {
        Modifier.margin(top = 8.px, bottom = 0.px)
    }

    cssRule("li") {
        Modifier.margin(bottom = 8.px)
    }

    cssRule("li:last-child") {
        Modifier.margin(bottom = 0.px)
    }

    cssRule("a") {
        Modifier.color(SitePalette.brand.primary).styleModifier {
            property("text-decoration-thickness", "1px")
            property("text-underline-offset", "0.2em")
        }
    }

    cssRule("a:hover") {
        Modifier.color(SitePalette.brand.secondary)
    }

    cssRule("a:focus-visible") {
        Modifier.styleModifier {
            property("outline", "2px solid #c811e2")
            property("outline-offset", "4px")
        }
    }

    cssRule("p:has(> a.markdown-button)") {
        Modifier.margin(0.px).lineHeight(0)
    }

    cssRule("a.markdown-button") {
        Modifier
            .sitePrimaryButtonAppearance()
            .siteButtonSize(SiteButtonSize.MEDIUM)
            .siteButtonText(SiteButtonSize.MEDIUM, SiteButtonLanguage.JP)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("box-sizing", "border-box")
                property("max-width", "100%")
                property("text-decoration", "none")
            }
    }

    cssRule("a.markdown-button::after") {
        Modifier.width(SiteButtonIconSizePx.px).height(SiteButtonIconSizePx.px).styleModifier {
            property("background", "url('$SiteButtonArrowPath') center / contain no-repeat")
            property("content", "\"\"")
            property("display", "block")
            property("flex", "0 0 ${SiteButtonIconSizePx}px")
            property("transition", "transform 160ms ease")
        }
    }

    cssRule("a.markdown-button:hover") {
        Modifier.siteButtonHover()
    }

    cssRule("a.markdown-button:hover::after") {
        Modifier.styleModifier { property("transform", "translateX(4px)") }
    }

    cssRule("a.markdown-button:active") {
        Modifier.sitePrimaryButtonActive()
    }

    cssRule("a.markdown-button[aria-disabled='true']") {
        Modifier.styleModifier {
            property("cursor", "default")
            property("filter", "grayscale(1)")
            property("opacity", "0.45")
            property("pointer-events", "none")
            property("text-decoration", "line-through")
        }
    }

    cssRule("img") {
        Modifier
            .display(DisplayStyle.Block)
            .width(100.percent)
            .maxWidth(800.px)
            .height(org.jetbrains.compose.web.css.keywords.auto)
            .margin(top = 0.px, bottom = 32.px)
            .styleModifier {
                property("margin-left", "auto")
                property("margin-right", "auto")
            }
    }

    cssRule("p:has(> img)") {
        Modifier.fillMaxWidth()
    }

    cssRule("strong") {
        Modifier.fontWeight(700)
    }

    cssRule("blockquote") {
        Modifier
            .margin(top = 0.px, bottom = 24.px)
            .padding(topBottom = 16.px, leftRight = 24.px)
            .backgroundColor(SitePalette.brand.softPink.toRgb().copyf(alpha = 0.35f))
            .borderLeft(4.px, LineStyle.Solid, SitePalette.brand.pink)
    }

    cssRule("blockquote > :last-child") {
        Modifier.margin(bottom = 0.px)
    }

    cssRule("code") {
        Modifier
            .color(Colors.Black.toRgb().copyf(alpha = 0.8f))
            .fontWeight(700)
    }

    cssRule("pre") {
        Modifier
            .fillMaxWidth()
            .margin(top = 0.px, bottom = 32.px)
            .overflow { x(Overflow.Auto) }
    }

    cssRule("pre > code") {
        Modifier
            .display(DisplayStyle.Block)
            .fillMaxWidth()
            .padding(16.px)
            .backgroundColor(SitePalette.nearBackground)
            .border(1.px, LineStyle.Solid, SitePalette.brand.border)
            .borderRadius(8.px)
            .fontSize(14.px)
            .styleModifier { property("box-sizing", "border-box") }
    }
}

@InitRoute
fun initMarkdownLayout(ctx: InitRouteContext) {
    val title = ctx.markdown!!.frontMatter["title"]?.singleOrNull()
    require(title != null) { "Markdown file must set \"title\" in frontmatter" }
    val subtitle = ctx.markdown!!.frontMatter["subtitle"]?.singleOrNull()
    val description = ctx.markdown!!.frontMatter["description"]?.singleOrNull() ?: SiteDescription

    ctx.data.add(PageLayoutData(title, description, fullWidth = true))
    ctx.data.add(MarkdownPageData(title, subtitle))
}

private fun String.containsJapaneseCharacters(): Boolean = any { character -> character.code > 0x7F }

@Composable
private fun MarkdownPageHero(data: MarkdownPageData) {
    Box(MarkdownPageHeroStyle.toModifier(), contentAlignment = Alignment.Center) {
        Column(
            ContentContainerStyle.toModifier().then(MarkdownPageHeroContentStyle.toModifier()),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
        ) {
            val titleStyle = if (data.title.containsJapaneseCharacters()) {
                MarkdownJapaneseHeroTitleStyle
            } else {
                MarkdownEnglishHeroTitleStyle
            }
            H1(titleStyle.toModifier().toAttrs()) {
                Text(data.title)
            }
            data.subtitle?.let { subtitle ->
                SpanText(subtitle, MarkdownHeroSubtitleStyle.toModifier())
            }
        }
    }
}

@Composable
@Layout(".components.layouts.PageLayout")
fun MarkdownLayout(ctx: PageContext, content: @Composable () -> Unit) {
    val data = ctx.data.getValue<MarkdownPageData>()
    Column(Modifier.fillMaxWidth()) {
        MarkdownPageHero(data)
        Column(MarkdownContentSectionStyle.toModifier(), horizontalAlignment = Alignment.CenterHorizontally) {
            Column(ContentContainerStyle.toModifier().then(MarkdownStyle.toModifier())) {
                content()
            }
        }
    }
}
